import { FONT_WEIGHT } from '@app/styles/themes/constants';
import { Card as AntCard } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';

/** Deal-room orange — same accent as the landing page chat button (FixedBtn). */
const ACCENT = '#ce6239';
const ACCENT_DARK = '#b3512d';
const NAVY = '#1a295a';
/** NAVY at 12% — the badge wash, kept in sync with the label colour above it. */
const NAVY_WASH = 'rgba(26, 41, 90, 0.12)';

/**
 * The default antd tooltip is a small translucent dark chip — unreadable for
 * the long Hebrew titles and descriptions these cards clamp. antd renders the
 * overlay into a body-level portal, so it cannot be reached from a styled
 * child; this global rule targets the overlayClassName the component passes.
 */
export const PodcastTooltipStyle = createGlobalStyle`
  .podcast-tooltip {
    max-width: 420px;

    .ant-tooltip-inner {
      background-color: ${NAVY};
      color: #fff;
      font-size: 14px;
      font-weight: ${FONT_WEIGHT.regular};
      line-height: 1.5;
      text-align: right;
      direction: rtl;
      padding: 10px 14px;
      border-radius: 10px;
      box-shadow: 0 8px 24px rgba(26, 41, 90, 0.28);
      /* Long descriptions arrive as one unbroken run of text. */
      white-space: pre-line;
      word-break: break-word;
    }

    .ant-tooltip-arrow-content {
      background-color: ${NAVY};
    }
  }

  @media screen and (max-width: 768px) {
    .podcast-tooltip {
      max-width: 80vw;
    }
  }
`;

export const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 8px;
  direction: rtl; /* Hebrew is right-to-left */

  @media screen and (max-width: 768px) {
    padding: 0;
  }
`;

export const Header = styled.div`
  margin-bottom: 20px;

  h2 {
    font-size: 32px;
    color: #1a295a;
    font-weight: ${FONT_WEIGHT.extremeBold};
    line-height: normal;
    margin: 0;
  }

  p {
    font-size: 18px;
    color: #1a295a;
    font-weight: ${FONT_WEIGHT.light};
    line-height: normal;
    margin: 4px 0 0;
  }

  @media screen and (max-width: 768px) {
    text-align: center;

    h2 {
      font-size: 18px;
    }

    p {
      font-size: 14px;
    }
  }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Card = styled(AntCard)`
  border-radius: 12px;
  border: 1px solid #c8c8c8;
  // box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  box-shadow: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${ACCENT};
    box-shadow: 0 10px 24px rgba(26, 41, 90, 0.14);
  }

  .ant-card-body {
    padding: 16px;
  }

  @media screen and (max-width: 768px) {
    .ant-card-body {
      padding: 10px;
    }
  }
`;

export const CardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    text-align: center;
  }
`;

export const TextWrapper = styled.div`
  flex: 1;
  /* Breathing room on both sides: away from the artwork (RTL start) and away
     from the listen pill (RTL end). */
  padding-right: 20px;
  padding-left: 40px;
  min-width: 0; /* lets the clamped children shrink instead of pushing the pill */
  color: #1a295a;
  font-weight: ${FONT_WEIGHT.light};

  @media screen and (max-width: 768px) {
    padding-right: 0;
    padding-left: 0;
  }
`;

/**
 * Titles and descriptions come from the scraped site and vary wildly in
 * length. Both are clamped to three rows with an ellipsis; the full text is
 * available in the hover tooltip the component wraps them in.
 */
const clampToThreeRows = `
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * The speaker chip above each episode title, sized like the one on
 * baroz.co.il/הפודקאסט but in the deal room's own blue — the same NAVY the
 * listen button's label and Spotify mark use — on a 12% wash of it.
 */
export const Tag = styled.span`
  display: inline-block;
  background-color: ${NAVY_WASH};
  color: ${NAVY};
  font-size: 12px;
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 12px;
  padding: 5px 12px;
  border-radius: 999px;
  margin-bottom: 8px;
  /* Long speaker lines keep the chip on one row instead of wrapping it. */
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Title = styled.h3`
  font-size: 18px;
  color: ${NAVY};
  font-weight: ${FONT_WEIGHT.extremeBold};
  line-height: 1.35;
  margin: 0 0 4px;
  white-space: pre-line;
  transition: color 0.25s ease;
  ${clampToThreeRows}

  ${Card}:hover & {
    color: ${ACCENT};
  }
`;

export const Description = styled.p`
  font-size: 14px;
  color: #1a295a;
  font-weight: ${FONT_WEIGHT.light};
  line-height: 1.4;
  margin: 0;
  white-space: pre-line;
  ${clampToThreeRows}
`;

/**
 * Episode artwork, sized like the video overlay on baroz.co.il: a 16:9 frame
 * that the YouTube player takes over in place when the viewer hits play.
 */
export const MediaWrapper = styled.div<{ $isPlaying?: boolean; $hasArtwork?: boolean }>`
  position: relative;
  /* Confines the play button's z-index to this frame. Without it the button
     shares the root stacking context with the landing page's fixed header
     (also z-index: 1) and, being later in the DOM, scrolled over the top of it. */
  isolation: isolate;
  flex-shrink: 0;
  /* Narrower than 16:9 but the full height is kept, so the embedded player
     stays large enough to watch in place. Artwork covers the frame; the
     YouTube player letterboxes itself inside it. */
  width: 340px;
  height: 225px;
  border-radius: 8px;
  overflow: hidden;
  /* Centres the placeholder icon; full-bleed artwork and the player still
     stretch to fill because they are sized at 100%. */
  display: flex;
  align-items: center;
  justify-content: center;
  /* Real artwork fills the frame edge to edge, so it sits on black. The
     placeholder is a small icon shown whole, so it gets a light card-like
     backdrop instead. */
  background-color: ${({ $hasArtwork }) => ($hasArtwork ? '#000' : '#f4f8fb')};

  img,
  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }

  img {
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  /* The placeholder is only 64px square — cover would blow it up and crop it.
     Contain it at its natural size, centred, so the whole icon reads. */
  ${({ $hasArtwork }) =>
    !$hasArtwork &&
    `
    img {
      width: auto;
      height: auto;
      max-width: 64px;
      max-height: 64px;
      margin: auto;
      object-fit: contain;
    }
  `}

  /* Darkens the artwork on hover so the play button reads clearly. Painted
     below the button, which is lifted with its own z-index. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.45) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  /* Once the player is open the frame belongs to YouTube — no zoom, no scrim.
     The placeholder is not zoomed either; there is no detail to reveal. */
  ${({ $isPlaying, $hasArtwork }) =>
    !$isPlaying &&
    $hasArtwork &&
    `
    &:hover img {
      transform: scale(1.06);
    }

    &:hover::after {
      opacity: 1;
    }
  `}

  @media screen and (max-width: 768px) {
    width: 100%;
    margin-bottom: 12px;
  }
`;

/** Centred play affordance, rendered only for episodes that have a video. */
export const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: ${ACCENT};
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 26px;
    height: 26px;
    /* No margin nudge: under the RTL container it pushed the glyph off centre.
       The flex centring above is the single source of position. */
    margin: 0;
    fill: currentColor;
  }

  &:hover,
  &:focus-visible {
    background-color: ${ACCENT_DARK};
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 6px 20px rgba(206, 98, 57, 0.55);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  @media screen and (max-width: 768px) {
    width: 52px;
    height: 52px;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

/**
 * The listen pill, matching baroz.co.il/הפודקאסט: solid orange, fully rounded,
 * white bold label with a play glyph beside it. antd renders an <a class="ant-btn">
 * when `href` is set, so the rules target .ant-btn rather than `button`.
 */
export const ButtonWrapper = styled.div`
  flex-shrink: 0;
  margin-left: 32px;

  .ant-btn {
    background-color: ${ACCENT};
    border: 0;
    border-radius: 100px;
    height: 41px;
    padding: 0 18px;
    font-size: 14px;
    font-weight: ${FONT_WEIGHT.bold};
    color: ${NAVY};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    box-shadow: none;
    transition: background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;

    /* Same 19px mark, in the same navy, as the reference site's button. */
    svg {
      width: 19px;
      height: 19px;
      fill: ${NAVY};
      flex-shrink: 0;
    }

    &:hover,
    &:focus {
      background-color: ${ACCENT_DARK};
      border: 0;
      color: ${NAVY};
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(241, 97, 34, 0.4);
    }
  }

  @media screen and (max-width: 768px) {
    margin-inline: auto;
    margin-top: 12px;
    margin-left: auto;
    width: 100%;
    max-width: 220px;

    .ant-btn {
      width: 100% !important;
      height: 42px;
      justify-content: center;
    }
  }
`;
